'use client';

import {
  type SidebarSection,
  type SidebarSectionGroup,
  visibleSidebarSections,
} from '@/elements/research/components/sidebarSections';
import { useAppSelector } from '@/lib/redux/hooks';
import { selectSidebarLayout } from '@/lib/redux/selectors/sidebarLayoutSelectors';
import {
  type LayoutGroupId,
  type SidebarLayoutState,
  isCustomGroupId,
} from '@/types/sidebarLayout';
import { useTranslations } from 'next-intl';

// Catalog default order of the built-in groups — 'top' first (the unlabeled
// primary cluster), matching today's fixed rail order.
const DEFAULT_GROUP_ORDER: readonly SidebarSectionGroup[] = [
  'top',
  'tools',
  'documents',
  'professionals',
  'practice',
];

// i18n key (in the `SidebarHeader` namespace) for each built-in group's
// header label. 'top' has no default label — it renders unlabeled unless the
// user explicitly renames it (see groupRenames).
const GROUP_HEADER_LABEL_KEYS: Record<SidebarSectionGroup, string | null> = {
  top: null,
  tools: 'groupTools',
  documents: 'groupDocuments',
  professionals: 'groupProfessionals',
  practice: 'mojaKancelaria',
};

export interface ResolvedLayoutItem {
  section: SidebarSection;
  hidden: boolean;
}

interface ResolvedGroupInternal {
  id: LayoutGroupId;
  isCustom: boolean;
  defaultLabelKey: string | null;
  // A rename override (built-in group) or the group's own label (custom
  // group). `null` means "use the default i18n label" (or no label at all
  // for an un-renamed 'top').
  renameOrCustomLabel: string | null;
  items: ResolvedLayoutItem[];
}

interface ResolveSidebarLayoutResult {
  groups: ResolvedGroupInternal[];
}

/**
 * Pure merge of the catalog (SIDEBAR_SECTIONS, already gated by
 * visibleSidebarSections) with the user's persisted layout overrides.
 * Independently unit-testable — no React/Redux/i18n dependency.
 *
 * Deliberately keeps hidden items in the output (rather than dropping them)
 * so the settings editor can still show/toggle them; live-rail consumers
 * filter hidden items out themselves (see toVisibleGroups below). Likewise a
 * built-in group only appears here when at least one gated-visible section
 * resolves into it (a group the user has no access to at all is never
 * offered as an edit target), while EVERY custom group the user created
 * always appears — even with zero items — so it stays visible as a drop
 * target in the editor until deleted.
 */
export function resolveSidebarLayout(
  sections: readonly SidebarSection[],
  layout: SidebarLayoutState
): ResolveSidebarLayoutResult {
  const buckets = new Map<
    LayoutGroupId,
    { item: ResolvedLayoutItem; order: number }[]
  >();

  sections.forEach((section, catalogIndex) => {
    const override = layout.items[section.key];
    const groupId: LayoutGroupId = override?.group ?? section.group;
    const order = override?.order ?? catalogIndex;
    const entry = {
      item: { section, hidden: override?.hidden ?? false },
      order,
    };
    const bucket = buckets.get(groupId);
    if (bucket) bucket.push(entry);
    else buckets.set(groupId, [entry]);
  });

  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => a.order - b.order);
  }

  // Full ordered group-id list: user's explicit groupOrder first, then the
  // catalog default order, then any custom groups not yet in groupOrder
  // (shouldn't normally happen — createGroup always appends to groupOrder —
  // but stays defensive), then any group id that only shows up as a bucket
  // key (e.g. a dangling override pointing at a deleted group — defensive,
  // see useSidebarLayout.test.ts).
  const seen = new Set<LayoutGroupId>();
  const orderedIds: LayoutGroupId[] = [];
  const pushId = (id: LayoutGroupId) => {
    if (seen.has(id)) return;
    seen.add(id);
    orderedIds.push(id);
  };
  layout.groupOrder.forEach(pushId);
  DEFAULT_GROUP_ORDER.forEach(pushId);
  layout.customGroups.forEach((g) => pushId(g.id));
  for (const id of buckets.keys()) pushId(id);

  const customGroupById = new Map(
    layout.customGroups.map((g) => [g.id, g] as const)
  );

  const groups: ResolvedGroupInternal[] = [];
  for (const id of orderedIds) {
    const bucket = buckets.get(id);
    if (isCustomGroupId(id)) {
      // Custom groups always survive, even with zero items, so a freshly
      // created empty one stays available as a drop target in the editor.
      groups.push({
        id,
        isCustom: true,
        defaultLabelKey: null,
        renameOrCustomLabel: customGroupById.get(id)?.label ?? null,
        items: (bucket ?? []).map((e) => e.item),
      });
      continue;
    }
    // A built-in group with literally no resolved sections (fully gated out
    // for this user, e.g. 'practice' for a non-practice user) is dropped
    // entirely — nothing to edit there.
    if (!bucket) continue;
    groups.push({
      id,
      isCustom: false,
      defaultLabelKey: GROUP_HEADER_LABEL_KEYS[id],
      renameOrCustomLabel: layout.groupRenames[id] ?? null,
      items: bucket.map((e) => e.item),
    });
  }

  return { groups };
}

export interface ResolvedSidebarGroup {
  id: LayoutGroupId;
  isCustom: boolean;
  // Header text for the live rail. `null` means render no header at all
  // (matches today's unlabeled 'top' cluster when un-renamed).
  label: string | null;
  // Header text for the settings editor — never null, so every group
  // (including an un-renamed 'top') stays editable/nameable.
  editorLabel: string;
  items: ResolvedLayoutItem[];
}

interface UseSidebarLayoutResult {
  groups: ResolvedSidebarGroup[];
}

/**
 * Single source of grouping/ordering truth for BOTH the expanded SidebarRail
 * and the collapsed SidebarActionList (replacing their previously-duplicated
 * sectionsByGroup/labeledGroupOrder/groupHeaderLabelKey logic), plus the
 * SidebarLayoutSettings editor — so all three can never drift apart.
 */
export function useSidebarLayout(opts: {
  isAnonymousMode?: boolean;
  practiceEnabled?: boolean;
  capabilities?: Record<string, boolean | number>;
  capabilitiesUnknown?: boolean;
}): UseSidebarLayoutResult {
  const t = useTranslations('SidebarHeader');
  const layout = useAppSelector(selectSidebarLayout);
  // The gate always runs first, unconditionally — layout customization only
  // ever reorders/regroups/hides within this already-filtered set, so a
  // capability/practice/auth-gated item can never be un-hidden by preference.
  const visible = visibleSidebarSections(opts);
  const { groups: internalGroups } = resolveSidebarLayout(visible, layout);

  const groups: ResolvedSidebarGroup[] = internalGroups.map((g) => {
    const defaultLabel = g.defaultLabelKey
      ? t(g.defaultLabelKey as Parameters<typeof t>[0])
      : null;
    return {
      id: g.id,
      isCustom: g.isCustom,
      label: g.renameOrCustomLabel ?? defaultLabel,
      editorLabel: g.renameOrCustomLabel ?? defaultLabel ?? t('groupTop'),
      items: g.items,
    };
  });

  return { groups };
}

/** Drops hidden items and any group left empty as a result — the view a live rail should render. Shared here so SidebarRail and SidebarActionList can't diverge on the filtering rule. */
export function toVisibleGroups(
  groups: readonly ResolvedSidebarGroup[]
): { id: LayoutGroupId; label: string | null; items: SidebarSection[] }[] {
  return groups
    .map((g) => ({
      id: g.id,
      label: g.label,
      items: g.items.filter((i) => !i.hidden).map((i) => i.section),
    }))
    .filter((g) => g.items.length > 0);
}
